// =============================================================================
// DELIVERY CONFIG READER (Lane BL) — serve the two admin-authored delivery-config
// reads from Supabase INSTEAD of the Google Sheet, behind DELIVERY_CONFIG_READ_HOSTS
// + ?deliveryconfigread. Ships gated FULLY OFF.
//
//   * getdeliveryplatforms      -> delivery_platforms_feed()        (migration 1220)
//   * getdeliveryorderguidance  -> delivery_order_guidance_feed()   (migration 1220)
//
// Both configs are GLOBAL (no per-site scoping — neither reader sends a site param).
//
// DRIFT GUARD (mandatory, mirrors Lane BK's compliance-config reader). The FOH order-log
// tile renders the platform picker + the ✅/❌ guidance from these lists, so a Supabase
// config that disagrees with the live Sheet would show the wrong platforms or the wrong
// guidance photo. So the reader serves Supabase ONLY when it matches the live Sheet
// (same platforms IN ORDER; same right/wrong image+caption). Any drift — or a Sheet we
// can't reach to verify — falls back to the Sheet (or null, so the tile keeps its
// built-in default). The image bytes stay on Drive; the Drive URL is compared + echoed
// byte-for-byte, never re-hosted.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { ensureAnonSession } from "./stockSync.js";
import { configAuthoritativeEnabled } from "./configAuthoritative.js";

// The read-flip gate — SEPARATE from any write gate, EMPTY by default. A flip is a
// reviewed one-line host add in daylight once 1220 is on prod, the config is loaded, and
// parity is CLEAN (SWITCHON_DELIVERY_CONFIG.md). ?deliveryconfigread=on|off overrides.
function hostGateEnabled(hosts, queryParam) {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get(queryParam);
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return hosts.has((window.location.hostname || "").toLowerCase());
}

const DELIVERY_CONFIG_READ_HOSTS = new Set([
  // "trafford.eatrolled.com",  // <- (the kitchen is on the Trafford host since 24 Sep 2026 — was mcr-kitchen) added ONLY at the delivery-config read flip (SWITCHON_DELIVERY_CONFIG.md)
]);
export function deliveryConfigReadEnabled() { return hostGateEnabled(DELIVERY_CONFIG_READ_HOSTS, "deliveryconfigread"); }

// The AUTHORITATIVE gate (Lane BV, 13 Sep 2026) — a SECOND, separate host set, EMPTY by
// default. When a host is listed here (or ?deliveryconfigauth=on on this tab) AND it is
// already on the read gate above, the two readers serve Supabase WITHOUT fetching or
// comparing the Sheet: no Apps Script call at all. This is the step that lets the Command
// Centre stop writing the Sheet (DELIVERY_CONFIG_SHEET_WRITE in rolled-admin #219) — with
// the drift guard still on, the first Sheet-less edit would make every flipped till fall
// back to the stale Sheet and the edit would never reach the order-log tile. If Supabase
// fails in this mode the reader returns null and the tile keeps its built-in default (its
// existing "call failed" path) — never a silent fallback to the Sheet.
// Precondition enforced in code (configAuthoritative.js): the host must already read
// Supabase; a listed host that isn't stays guarded and warns once. ?deliveryconfigauth=off
// always wins. Order: read gate → soak → THIS → then the CC Sheet-write flag
// (SWITCHON_DELIVERY_CONFIG.md).
const DELIVERY_CONFIG_AUTHORITATIVE_HOSTS = new Set([
  // "trafford.eatrolled.com",  // <- added ONLY at the authoritative step, AFTER the host is on DELIVERY_CONFIG_READ_HOSTS and has soaked (SWITCHON_DELIVERY_CONFIG.md)
]);
export function deliveryConfigAuthoritativeEnabled() {
  return configAuthoritativeEnabled({
    hosts: DELIVERY_CONFIG_AUTHORITATIVE_HOSTS, param: "deliveryconfigauth",
    readEnabled: deliveryConfigReadEnabled(),
    label: "delivery-config", readGate: "DELIVERY_CONFIG_READ_HOSTS / ?deliveryconfigread",
  });
}

// --- pure drift-guard helpers (exported for the network-free unit test) ----------------
export function platformsUsable(d) {
  return !!(d && typeof d === "object" && Array.isArray(d.platforms));
}
export function guidanceUsable(d) {
  return !!(
    d && typeof d === "object" &&
    d.guidance && typeof d.guidance === "object" &&
    d.guidance.right && typeof d.guidance.right === "object" &&
    d.guidance.wrong && typeof d.guidance.wrong === "object"
  );
}
// Order-sensitive signature (the index is baked into each line, like Lane BK's item[idx]),
// so a reordering of the platform list registers as drift. name/example/active are the
// load-bearing fields FOH reads; `sort` is captured implicitly by the ordering.
export function platformsSignature(d) {
  const arr = (d && Array.isArray(d.platforms)) ? d.platforms : [];
  return arr.map((p, i) =>
    "platform[" + i + "]: name=" + String(p && p.name != null ? p.name : "").trim() +
    " | example=" + String(p && p.example != null ? p.example : "").trim() +
    " | active=" + ((p && p.active) ? 1 : 0)
  );
}
export function guidanceSignature(d) {
  const g = (d && d.guidance) || {};
  const line = (s) => {
    const x = g[s] || {};
    return s + ": url=" + String(x.imageUrl != null ? x.imageUrl : "").trim() +
      " | caption=" + String(x.caption != null ? x.caption : "").trim();
  };
  return [line("right"), line("wrong")];
}
export function configParity(sheetSig, supaSig) {
  const sheetSet = new Set(sheetSig), supaSet = new Set(supaSig);
  const drift = [];
  for (const l of supaSig) if (!sheetSet.has(l)) drift.push("supabase has, sheet lacks -> " + l);
  for (const l of sheetSig) if (!supaSet.has(l)) drift.push("sheet has, supabase lacks -> " + l);
  return { clean: drift.length === 0, drift };
}

// AUTHORITATIVE read (Lane BV): Supabase IS the config; the Sheet is NOT fetched and NOT
// compared (sheetFetch is never touched). Returns the feed's object tagged
// _parity:"authoritative", or null on ANY failure — the caller then keeps its built-in
// default, exactly as it does when the Sheet call fails today. Never throws.
function warnAuthoritative(fn, why) {
  try { console.warn("[delivery-config] AUTHORITATIVE read of " + fn + " failed — keeping the tile's built-in default (no Sheet fallback in this mode): " + why); } catch (e) {}
}
async function readAuthoritative(fn, usable, rpc, ensureSession) {
  try {
    await ensureSession();
    const { data, error } = await rpc(fn);
    if (error) { warnAuthoritative(fn, "supabase read failed: " + (error.message || error)); return null; }
    if (!usable(data)) { warnAuthoritative(fn, "supabase returned an empty/invalid config"); return null; }
    return Object.assign({}, data, { _source: "supabase", _parity: "authoritative" });
  } catch (e) { warnAuthoritative(fn, "threw: " + (e && e.message ? e.message : e)); return null; }
}

// The readers. Return the config object to serve (Supabase when CLEAN, else the Sheet), or
// null only when the Sheet can't be reached to verify — the FOH caller then keeps its
// built-in default (its .then early-returns on a falsy/empty result). NEVER serves a config
// that disagrees with the live Sheet. In AUTHORITATIVE mode (Lane BV — host listed in
// DELIVERY_CONFIG_AUTHORITATIVE_HOSTS or ?deliveryconfigauth=on, AND the read gate on)
// Supabase is served without any Sheet fetch/compare, and null on failure.
// `authoritative` / `rpc` / `ensureSession` are injectable for the network-free unit test;
// their defaults are exactly today's calls.
export async function readDeliveryPlatforms({
  sheetFetch = null,
  authoritative = deliveryConfigAuthoritativeEnabled(),
  rpc = (n, a) => supabase.rpc(n, a),
  ensureSession = ensureAnonSession,
} = {}) {
  if (authoritative === true) return readAuthoritative("delivery_platforms_feed", platformsUsable, rpc, ensureSession);
  const sheetOnce = async () => {
    if (typeof sheetFetch !== "function") return null;
    try { return await sheetFetch(); } catch (e) { return null; }
  };
  let sb = null;
  try {
    await ensureSession();
    const { data, error } = await rpc("delivery_platforms_feed");
    if (error) return sheetOnce();
    if (!platformsUsable(data)) return sheetOnce();
    sb = data;
  } catch (e) { return sheetOnce(); }

  const sheet = await sheetOnce();
  if (!platformsUsable(sheet)) return null; // can't reach the Sheet to verify -> keep the default
  const p = configParity(platformsSignature(sheet), platformsSignature(sb));
  if (!p.clean) return Object.assign({}, sheet, { _source: "sheet", _parity: "drift" });
  return Object.assign({}, sb, { _source: "supabase", _parity: "clean" });
}

export async function readDeliveryGuidance({
  sheetFetch = null,
  authoritative = deliveryConfigAuthoritativeEnabled(),
  rpc = (n, a) => supabase.rpc(n, a),
  ensureSession = ensureAnonSession,
} = {}) {
  if (authoritative === true) return readAuthoritative("delivery_order_guidance_feed", guidanceUsable, rpc, ensureSession);
  const sheetOnce = async () => {
    if (typeof sheetFetch !== "function") return null;
    try { return await sheetFetch(); } catch (e) { return null; }
  };
  let sb = null;
  try {
    await ensureSession();
    const { data, error } = await rpc("delivery_order_guidance_feed");
    if (error) return sheetOnce();
    if (!guidanceUsable(data)) return sheetOnce();
    sb = data;
  } catch (e) { return sheetOnce(); }

  const sheet = await sheetOnce();
  if (!guidanceUsable(sheet)) return null;
  const g = configParity(guidanceSignature(sheet), guidanceSignature(sb));
  if (!g.clean) return Object.assign({}, sheet, { _source: "sheet", _parity: "drift" });
  return Object.assign({}, sb, { _source: "supabase", _parity: "clean" });
}
